import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faCalendarPlus,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { Alert, Button } from 'react-daisyui';
import { useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { ICalendar } from '@types';
import CalendarModal from '@/components/CalendarModal';
import { get } from '@/utils/queries';
import { DashboardCard } from '@/components/DashboardCard';
import { useUserContext } from '@/context/UserContext';
import { alertLoadingMessage } from '@/utils/alerts';
import CalendarList from './CalendarList';
import { useSyncOutlook } from '../../hooks/useSyncOutlook';

// eslint-disable-next-line import/prefer-default-export
export function Calendar() {
  const { impersonatingAccountId } = useUserContext();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data: calendarData } = get(
    `calendar/getCalendarEvents/${impersonatingAccountId as number}`
  );
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { mutate } = useSyncOutlook(impersonatingAccountId as number);

  const onShowModal = (show = true) => {
    setShowModal(show);
  };

  const sync = async (): Promise<void> => {
    setIsLoading(true);
    await mutate();
    alertLoadingMessage(
      'Synchronization started, it might take several minutes.'
    );
  };

  return (
    <DashboardCard data-testid='calender-widget'>
      <ToastContainer />
      <div className='grid grid-cols-2'>
        <div className='flex'>
          <FontAwesomeIcon
            icon={faCalendar}
            size='2x'
            className='mr-5 text-primary'
          />
          <h3 className='font-bold text-base'>On the Horizon</h3>
        </div>

        <div className='text-right'>
          <Button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={sync}
            size='xs'
            data-testid='file-upload-button'
            className='btn-accent mr-[-6px]'
          >
            <FontAwesomeIcon
              data-testid='refresh-calendar'
              className={`mr-2 ${isLoading ? 'spinning' : ''}`}
              icon={faRefresh}
              size='lg'
              title='Refresh'
            />
            Refresh
          </Button>
        </div>
      </div>
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
      {calendarData?.calendars && calendarData?.calendars.length > 0 && (
        <>
          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */}
          {Object.values(calendarData?.calendars as unknown as ICalendar).map(
            (calendar) => (
              <CalendarList
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                key={calendar?.id}
                calendar={calendar as unknown as ICalendar}
              />
            )
          )}
          <div className='flex items-center mt-3'>
            <FontAwesomeIcon
              icon={faCalendarPlus}
              size='1x'
              className='mr-5 text-accent'
            />
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
            <p
              className='underline text-base hover:text-accent cursor-pointer'
              onClick={() => onShowModal()}
            >
              Add reminders to your calendar
            </p>
          </div>
          {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
          <CalendarModal
            clientId={(impersonatingAccountId as number)?.toString()}
            isOpen={showModal}
            onClose={() => setShowModal(false)}
          />
        </>
      )}
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
      {calendarData?.calendars.length === 0 && (
        <Alert
          className='rounded-2xl opacity-60 text-sm text-left w-full text-sm mt-4'
          icon={<ExclamationCircleIcon className='w-8 h-8' />}
          status='warning'
        >
          <div>There are no calendar events</div>
        </Alert>
      )}
    </DashboardCard>
  );
}
